/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import ExpandIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import { Header } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { Onboarding } from '@atlassian/jira-portfolio-3-onboarding/src/ui/onboarding/index.tsx';
import { useRedirectToSpaPlanPage } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/redirect-to-spa-plan-page/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { PLAN_MENU_ANALYTICS } from '../../../common/constants.tsx';
import { ArchivePlanModal } from './archive-modal/index.tsx';
import { TRASH, ARCHIVE, DUPLICATE, GETTING_STARTED_URL } from './constants.tsx';
import { DuplicatePlanModal } from './duplicate-modal/index.tsx';
import messages from './messages.tsx';
import NudgeProvider from './nudge/index.tsx';
import { TrashPlanModal } from './trash-modal/index.tsx';

type Props = {
	planId?: string | null;
	scenarioId?: string | null;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	onPlanStatusUpdated: () => void;
};

type ModalState = typeof DUPLICATE | typeof TRASH | typeof ARCHIVE | undefined;

export const PlanMenuHeader = ({
	planId,
	scenarioId,
	planTitle,
	isReadOnly,
	isArchived,
	onPlanStatusUpdated,
}: Props) => {
	const { formatMessage } = useIntl();
	const redirectToSpaPlanPage = useRedirectToSpaPlanPage();
	const [modalState, setModalState] = useState<ModalState>(undefined);
	const resetModalState = () => setModalState(undefined);

	const [onboarding, setOnboarding] = useState<string>();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onPlanMenuButtonClicked = useCallback(() => {
		const planMenuButtonClickAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'planMenuButton',
		});
		fireUIAnalytics(planMenuButtonClickAnalyticsEvent, PLAN_MENU_ANALYTICS);
	}, [createAnalyticsEvent]);

	const onHelpLinkClicked = useCallback(() => {
		const helpLinkMenuItemClickAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'helpLinkMenuItem',
		});
		fireUIAnalytics(helpLinkMenuItemClickAnalyticsEvent, PLAN_MENU_ANALYTICS);
	}, [createAnalyticsEvent]);

	const onTakeTourClicked = useCallback(() => {
		const takeTourMenuItemClickAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'takeTourMenuItem',
		});
		fireUIAnalytics(takeTourMenuItemClickAnalyticsEvent, PLAN_MENU_ANALYTICS);
		redirectToSpaPlanPage('/timeline');
		setOnboarding(Math.random().toString(36).substring(2));
	}, [createAnalyticsEvent, redirectToSpaPlanPage]);

	const onDuplicateClicked = useCallback(() => {
		setModalState(DUPLICATE);
		const duplicateMenuItemClickAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'duplicateModalMenuItem',
		});
		fireUIAnalytics(duplicateMenuItemClickAnalyticsEvent, PLAN_MENU_ANALYTICS);
	}, [createAnalyticsEvent]);

	const onDeleteClicked = useCallback(() => {
		setModalState(TRASH);
		const deleteMenuItemClickAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'deleteModalMenuItem',
		});
		fireUIAnalytics(deleteMenuItemClickAnalyticsEvent, PLAN_MENU_ANALYTICS);
	}, [createAnalyticsEvent]);

	const onArchiveRestoreClicked = useCallback(() => {
		setModalState(ARCHIVE);
		const getArchiveOrRestoreEvent = () =>
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: isArchived ? 'restoreModalMenuItem' : 'archiveModalMenuItem',
			});
		fireUIAnalytics(getArchiveOrRestoreEvent(), PLAN_MENU_ANALYTICS);
	}, [createAnalyticsEvent, isArchived]);

	const closeOnboarding = useCallback(
		(options?: { silent: boolean }) => !options?.silent && setOnboarding(undefined),
		[],
	);

	// Most plan operations aren't possible without a plan or scenario id, but we can still show *something*
	if (planId === undefined || scenarioId === undefined) {
		return <Header description={formatMessage(messages.plan)}>{planTitle}</Header>;
	}

	return (
		<Header description={formatMessage(messages.plan)}>
			<span css={flexContainerStyles}>
				<span css={textOverflowStyles} aria-live="polite" aria-atomic="true">
					{planTitle}
				</span>
				<DropdownMenu
					onOpenChange={({ isOpen }) => {
						if (isOpen) {
							onPlanMenuButtonClicked();
						}
					}}
					trigger={({ triggerRef, ...props }) => (
						<NudgeProvider>
							{isVisualRefreshEnabled() ? (
								<IconButton
									{...props}
									appearance="subtle"
									spacing="compact"
									icon={() => <ChevronDownIcon label="" color="currentColor" />}
									ref={triggerRef}
									label={formatMessage(messages.triggerLabel)}
								/>
							) : (
								<Button
									{...props}
									ref={triggerRef}
									aria-label={formatMessage(messages.triggerLabel)}
									iconAfter={<ExpandIcon spacing="spacious" label="" />}
									spacing="none"
									appearance="subtle"
								/>
							)}
						</NudgeProvider>
					)}
					placement="bottom-end"
					shouldFlip={false}
				>
					<DropdownItem
						onClick={onHelpLinkClicked}
						href={GETTING_STARTED_URL}
						target="_blank"
						elemAfter={<ShortcutIcon LEGACY_size="small" label="" />}
					>
						{formatMessage(messages.help)}
					</DropdownItem>
					<DropdownItem onClick={onTakeTourClicked}>
						{formatMessage(messages.takeTour)}
					</DropdownItem>
					{!isReadOnly && (
						<DropdownItemGroup hasSeparator>
							<DropdownItem onClick={onDuplicateClicked}>
								{formatMessage(messages.duplicate)}
							</DropdownItem>
							<DropdownItem onClick={onDeleteClicked}>{formatMessage(messages.trash)}</DropdownItem>
							<DropdownItem onClick={onArchiveRestoreClicked}>
								{isArchived ? formatMessage(messages.restore) : formatMessage(messages.archive)}
							</DropdownItem>
						</DropdownItemGroup>
					)}
				</DropdownMenu>
			</span>
			{modalState === DUPLICATE && (
				<DuplicatePlanModal
					planId={Number(planId)}
					planTitle={planTitle}
					setClosed={resetModalState}
				/>
			)}
			{modalState === TRASH && (
				<TrashPlanModal planId={Number(planId)} setClosed={resetModalState} />
			)}
			{modalState === ARCHIVE && (
				<ArchivePlanModal
					planId={Number(planId)}
					scenarioId={Number(scenarioId)}
					planTitle={planTitle}
					isArchived={isArchived}
					setClosed={resetModalState}
					onPlanStatusUpdated={onPlanStatusUpdated}
				/>
			)}
			{onboarding && <Onboarding visible key={onboarding} onClose={closeOnboarding} />}
		</Header>
	);
};

PlanMenuHeader.defaultProps = {
	planId: undefined,
	scenarioId: undefined,
	isReadOnly: true,
};

const flexContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		flex: '0 0 auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'button:focus, button:focus-visible': {
		outlineOffset: 0,
	},
	paddingTop: token('space.025'),
	paddingRight: 0,
	paddingBottom: token('space.025'),
	paddingLeft: 0,
});

const textOverflowStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	marginRight: token('space.075'),
});
